<template>
	<aside v-if="videoList.length" class="video-sidebar">
		<ul class="video-sidebar__list">
			<li
				v-for="(item, index) in videoList"
				:key="item.id"
				:ref="el => bindRecommendRef(el, item, index)"
				class="video-sidebar__item"
				@click="handleToDetail(item, index)"
			>
				<div class="video-sidebar__thumb">
					<img :src="item.cover" :alt="item.title" class="video-sidebar__cover" />
				</div>
				<div class="video-sidebar__info">
					<p class="video-sidebar__title">{{ item.title }}</p>
					<div class="video-sidebar__meta">
						<img :src="videoIcon" alt="" class="video-sidebar__meta-icon" />
						<span>{{ formatViewCount(item.view_count) }}</span>
					</div>
				</div>
			</li>
		</ul>
	</aside>
</template>

<script setup>
import { computed, inject } from "vue";
import { getSquareRecommend, playContent } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import {
	buildRelatedItemParams,
	useDetailListImpression,
} from "../../../composables/useSquareDetailTracking";
import videoIcon from "../../../images/vedio-xx.png";

const MAX_VIDEO_COUNT = 7;

const props = defineProps({
	squareInfo: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const { bindItemRef: bindRecommendRef } = useDetailListImpression({
	logDetailEvent,
	impressionEvent: "media_related_content_impression",
	getItemParams: (item, index) => buildRelatedItemParams(props.squareInfo, item, index + 1),
});

const { data: recommendData } = await useLazyAsyncData(
	`square-detail-recommend-${props.squareId}`,
	async () => {
		const info = props.squareInfo;
		const squareId = props.squareId || info?.id;
		if (!squareId || !info?.uid) return [];

		const res = await getSquareRecommend({
			square_id: String(squareId),
			uid: String(info.uid),
			class_id: String(info.class_id ?? 0),
		});

		const list = res?.data?.horiz_video;
		return Array.isArray(list) ? list.slice(0, MAX_VIDEO_COUNT) : [];
	},
	{
		default: () => [],
		watch: [() => props.squareInfo?.id, () => props.squareId],
	}
);

const videoList = computed(() => recommendData.value || []);

const formatViewCount = count => {
	const value = Number(count) || 0;
	if (value >= 10000) {
		if (locale.value === "en") {
			return t("square.views.count", { count: `${(value / 1000).toFixed(1)}K` });
		}
		return t("square.views.tenThousand", { count: (value / 10000).toFixed(1) });
	}
	return t("square.views.count", { count: value });
};

const handleToDetail = async (item, index) => {
	if (!item?.id) return;
	if (logDetailEvent) {
		logDetailEvent(
			"media_related_content_click",
			buildRelatedItemParams(props.squareInfo, item, index + 1)
		);
	}
	await navigateTo(`/seer/square-detail/${item.id}`);
	await playContent({ square_id: item.id });
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.video-sidebar {
	&__list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__item {
		display: flex;
		gap: 12px;
		margin-bottom: 20px;
		cursor: pointer;

		&:last-child {
			margin-bottom: 0;
		}

		&:hover .video-sidebar__title {
			color: #f2af19;
		}
	}

	&__thumb {
		flex-shrink: 0;
		width: 273px;
		height: 153px;
		border-radius: 12px;
		overflow: hidden;
		background: #f0f0f0;
	}

	&__cover {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	&__info {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	&__title {
		margin: 0 0 8px;
		font-size: 20px;
		font-weight: 500;
		color: #333333;
		line-height: 1.5;
		transition: color 0.2s ease;
		@include line-clamp(4);
	}

	&__meta {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 18px;
		color: #999999;
	}

	&__meta-icon {
		width: 24px;
		height: 24px;
		object-fit: contain;
		opacity: 0.6;
	}
}
</style>
